<script setup>
import { computed } from "vue";
import { BetSlip } from "@/stores/BetSlip";

const props = defineProps({
	boost: {
		type: Object,
		required: true
	}
});

const betslipStore = BetSlip();

const isSuperBoost = computed(() => {
	if (typeof props.boost.is_super_boost === "boolean") {
		return props.boost.is_super_boost;
	}
	if (props.boost.original_odds && props.boost.boosted_odds) {
		const diff = props.boost.boosted_odds - props.boost.original_odds;
		const pct = Math.round((diff / props.boost.original_odds) * 100);
		return pct >= 20;
	}
	return false;
});

const formatOdds = (val) => {
	if (val == null) return "";
	const num = Number(val);
	return isNaN(num) ? val : num.toFixed(2);
};

const addToBetslip = () => {
	if (!props.boost?.odd_uuid || !props.boost?.fixture_id) return;
	betslipStore.toggleToBetslip(
		{
			odd_uuid: props.boost.odd_uuid,
			fixture_id: props.boost.fixture_id,
			campaign_id: props.boost.campaign_id || props.boost.outcome?.campaign_id || null,
		},
		null,
		null,
		true
	);
};
</script>

<template>
	<div class="bet-booster-card-footer">
		<button
			type="button"
			class="odds-button"
			:class="{ 'odd-selected': betslipStore.isBoostedOddInBetslip(boost.odd_uuid) }"
			@click="addToBetslip"
		>
			<span v-if="boost.original_odds" class="original-odds">
				{{ formatOdds(boost.original_odds) }}
			</span>
			<span v-if="boost.original_odds && boost.boosted_odds" class="arrow-sep">
				»
			</span>
			<span
				v-if="boost.boosted_odds"
				class="boosted-odds"
				:class="{ 'super-odd': isSuperBoost, 'standard-odd': !isSuperBoost }"
			>
				{{ formatOdds(boost.boosted_odds) }}
			</span>
		</button>
	</div>
</template>

<style lang="scss" scoped>
.bet-booster-card-footer {
	padding: 0 12px 10px 12px;
	width: 100%;
}

.odds-button {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	background: var(--off-gray);
	border: 1px solid rgba(0, 0, 0, 0.06);
	border-radius: 7px;
	padding: 7px 12px;
	cursor: pointer;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	transition: all 0.2s ease;

	&:hover {
		transform: translateY(-1px);
		box-shadow: 0 3px 8px rgba(0, 0, 0, 0.09);
		background: var(--primary);

		.boosted-odds.standard-odd {
			color: var(--secondary);
		}
	}

	&:active,
	&.odd-selected {
		transform: translateY(0);
		background: var(--primary);

		.boosted-odds.standard-odd {
			color: var(--secondary);
		}
	}

	.original-odds {
		font-size: 11px;
		font-weight: 600;
		color: var(--dark-gray);
		text-decoration: line-through;
	}

	.arrow-sep {
		font-size: 13px;
		color: var(--dark-gray);
		font-weight: 700;
	}

	.boosted-odds {
		font-size: 13.5px;
		font-weight: 800;

		&.super-odd {
			background: linear-gradient(135deg, var(--secondary) 0%, var(--red) 100%);
			-webkit-background-clip: text;
			background-clip: text;
			-webkit-text-fill-color: transparent;
			color: var(--odd-price-down);
		}

		&.standard-odd {
			color: var(--primary);
			transition: color 0.2s ease;
		}
	}
}

@media (max-width: 991px) {
	.bet-booster-card-footer {
		padding: 0 10px 8px 10px;
	}

	.odds-button {
		padding: 6px 10px;
		border-radius: 6px;
		gap: 5px;

		.original-odds {
			font-size: 10.5px;
		}

		.arrow-sep {
			font-size: 9.5px;
		}

		.boosted-odds {
			font-size: 12.5px;
		}
	}
}

@media (max-width: 425px) {
	.bet-booster-card-footer {
		padding: 0 10px 8px 10px;
	}

	.odds-button {
		padding: 6px 10px;
	}
}
</style>
